// @flow
import React, { Component } from 'react';
import styled from '@emotion/styled';
import { colors } from '@atlaskit/theme';
import { grid, borderRadius } from './constants';
import { Draggable } from 'react-beautiful-dnd';
import type { DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import CardsList from './primatives/cardsList';
import Title from './primatives/title';
import deleteImg from "./assets/delete.png";
import editingImg from "./assets/editing.png";
import saveImg from "./assets/save.png";
import cancelImg from "./assets/cancel.png";
import lockImg from "./assets/lock.png";
import ColumnService from "./../services/column.js";
import {getBoardIdFromURL} from "./../util/common.js";
import { confirmDialogA11y } from "./../util/confirmDialogA11y.jsx";
import { announce } from "./../util/liveRegion.js";
import "./css/tooltip.css";

const Container = styled.section`
  margin: ${grid}px;
  display: flex;
  flex-direction: column;
  width: 18vw;  //TODO: depending on max # of columns per board (currently 5 columns max)
  min-width: 320px; 
  max-width: 400px;
`;

const Header = styled.div`
  display: flex;
  font-family: 'Segoe UI Regular'!important;
  align-items: center;
  justify-content: center;
  border-top-left-radius: ${borderRadius}px;
  border-top-right-radius: ${borderRadius}px;
  background-color: #f7f5f4;
  transition: background-color 0.2s ease;
  // &:hover {
  //   background-color: #ffd2c3;
  // }
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const SideButtons = styled.div`
  text-align: right;
  white-space: nowrap;
  padding-right: 10px;
  margin-top: 5px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
 `;

 const ActionIcon = styled.img`
  height:  ${props => props.height ? props.height : "16"}px;
  padding-top:4px;
  display: inline-block;
  opacity: 0.5;
  transition: all 0.2s ease;
  padding-right: 5px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  & img {
    opacity: 0.5;
    transition: opacity 0.2s ease;
  }

  &:hover img,
  &:focus img {
    opacity: 1;
  }

  &:focus {
    outline: 2px solid #0052CC;
    outline-offset: 2px;
    border-radius: 3px;
  }
`;

const MoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  font-weight: 600;
  color: #5c6263;
  opacity: 0.5;
  transition: opacity 0.2s ease;
  padding: 2px 5px;
  display: inline-flex;
  align-items: center;
  font-family: inherit;

  &:hover,
  &:focus {
    opacity: 1;
  }

  &:focus {
    outline: 2px solid #0052CC;
    outline-offset: 2px;
    border-radius: 3px;
  }
`;

const ColumnTitleTextbox = styled.input`
  width: 100%;
  margin-left: 15px;
  margin-right: 15px;
  margin-bottom: 5px;
  outline: none;
  border: none;
  height: 38px;
  margin-top: 8px;
  cursor: auto!important;
  font-family: 'Segoe UI Regular';
`;

type Props = {|
  title: string,
  cards: Quote[],
  index: number,
  isScrollable?: boolean,
  isCombineEnabled?: boolean,
  useClone?: boolean,
|};

export default class Column extends Component<Props> {
  constructor(props) {
    super(props);
    this.state = {
      isEditing: false,
      editingTitle: props.title
    }
    this._titleInputEl = null;
    this._editButtonEl = null;
  }

  focusAfterRender = (getEl) => {
    requestAnimationFrame(() => {
      const el = getEl();
      if (el && typeof el.focus === 'function') el.focus();
    });
  }

  onColumnEditButtonClick = () => {
    this.setState({isEditing: true}, () => {
      this.focusAfterRender(() => {
        const input = this._titleInputEl;
        if (input && typeof input.select === 'function') input.select();
        return input;
      });
    });
  }

  onColumnSaveClick = async () => {
    // validate title is correct5
    if (!this.state.editingTitle?.trim()?.length > 0 ||
      this.state.editingTitle?.trim()?.length > 30) {
      return;
    }
    this.setState({isEditing: false}, () => {
      this.focusAfterRender(() => this._editButtonEl);
    });
    await ColumnService.update(getBoardIdFromURL(), this.props.columnId, this.state.editingTitle?.trim());
  }

  onColumnEditCancel = async () => {
    this.setState({isEditing: false, editingTitle: this.props.title}, () => {
      this.focusAfterRender(() => this._editButtonEl);
    });
  }

  onColumnDeleteClick = () => {
    this.setState({isEditing: true});
  }

  handleTitleTextBoxKeyPress = (event) => {
    if(event.key === 'Enter'){
      this.onColumnSaveClick();
    }
  }

  onColumnDeleteButtonClick = async () => {
    confirmDialogA11y({
      title: "Careful! You're about to delete this column with ALL its cards. You sure?",
      buttons: [
        { label: 'No', onClick: () => {} },
        { label: 'Yes', onClick: async () => await this.deleteColumn() }
      ]
    });
  }

  deleteColumn = async () => {
    const titleSnapshot = this.props.title;
    await ColumnService.delete(getBoardIdFromURL(), this.props.columnId);
    announce(`Column ${titleSnapshot} deleted`);
  }

  onMoveColumnLeft = () => {
    if (this.props.onMoveColumn) this.props.onMoveColumn(this.props.columnId, "left");
  }

  onMoveColumnRight = () => {
    if (this.props.onMoveColumn) this.props.onMoveColumn(this.props.columnId, "right");
  }

  render() {
    const { isEditing, editingTitle } = this.state;
    const { title, columnId, cards, index, isFilterActive, isFirstColumn, isLastColumn, onMoveCard, previousColumnTitle, nextColumnTitle } = this.props;
    const lockedColumnTooltip = "Re-ordering columns and cards is disabled when filters are in use"
    const canMoveColumns = !isFilterActive;
    const titleId = `column-title-${columnId}`;
    return (
      <Draggable draggableId={columnId} index={index} isDragDisabled={isFilterActive}>
        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
          <Container
            ref={provided.innerRef}
            {...provided.draggableProps}
            aria-labelledby={!isEditing ? titleId : undefined}
            aria-label={isEditing ? `Editing column: ${title}` : undefined}
            aria-roledescription="column"
          >
            <div className="columnHeaderWrapper">
              <Header isDragging={snapshot.isDragging} className="">
                {isEditing && <ColumnTitleTextbox isDragging={snapshot.isDragging}
                  {...provided.dragHandleProps}
                  role={undefined}
                  aria-describedby={undefined}
                  ref={(el) => { this._titleInputEl = el; }}
                  aria-label="Column title" value={editingTitle} onKeyPress={this.handleTitleTextBoxKeyPress} onChange={(e) => this.setState({editingTitle: e.target.value})}></ColumnTitleTextbox>}
                {!isEditing && <Title
                  id={titleId}
                  isDragging={snapshot.isDragging}
                  {...provided.dragHandleProps}
                  role={undefined}
                  tabIndex={undefined}
                  aria-describedby={undefined}
                >
                  {title}&nbsp;
                  {isFilterActive && <abbr {...(index === 0 ? ({"right-data-title": lockedColumnTooltip}) : ({"center-data-title": lockedColumnTooltip}))}><ActionIcon src={lockImg} height={13} alt="Reordering disabled while filters are active" /></abbr>}
                </Title>}
                {isEditing &&
                <SideButtons>
                  <IconButton type="button" aria-label={`Save column title for ${title}`} onClick={this.onColumnSaveClick}>
                    <ActionIcon src={saveImg} alt="" aria-hidden="true" />
                  </IconButton>
                  <IconButton type="button" aria-label="Cancel editing column title" onClick={this.onColumnEditCancel}>
                    <ActionIcon src={cancelImg} height={13} alt="" aria-hidden="true" />
                  </IconButton>
                </SideButtons>}
                {!isEditing &&
                <SideButtons className="columnEditButton">
                  <IconButton type="button" aria-label="Change column name" title="Change column name" onClick={this.onColumnEditButtonClick} ref={(el) => { this._editButtonEl = el; }}>
                    <ActionIcon src={editingImg} alt="" aria-hidden="true" />
                  </IconButton>
                  <IconButton type="button" aria-label="Delete this column" title="Delete this column" onClick={this.onColumnDeleteButtonClick}>
                    <ActionIcon src={deleteImg} alt="" aria-hidden="true" />
                  </IconButton>
                </SideButtons>}

              </Header>
            </div>
            <CardsList
              listId={columnId}
              listType="QUOTE"
              isFilterActive={isFilterActive}
              mapOfAllTags={this.props.mapOfAllTags}
              cards={cards}
              internalScroll={this.props.isScrollable}
              isCombineEnabled={Boolean(this.props.isCombineEnabled)}
              useClone={Boolean(this.props.useClone)}
              onMoveCard={onMoveCard}
              isFirstColumn={isFirstColumn}
              isLastColumn={isLastColumn}
              previousColumnTitle={previousColumnTitle}
              nextColumnTitle={nextColumnTitle}
              columnTitle={title}
            />
          </Container>
        )}
      </Draggable>
    );
  }
}
